import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api'
import styles from './CommunityFootprintMap.module.scss'

const DEFAULT_ZOOM = 10
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%', minHeight: 200 }
const PIN_ICON_SIZE = { width: 40, height: 40 }

// map component for both profiles; pins from community_map_pins API
export default function CommunityFootprintMap({
  center,
  loading,
  profileType,
  pins = [],
  className,
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  const markerRefs = useRef({})
  const [selectedPinEventId, setSelectedPinEventId] = useState(null)

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-profile',
    googleMapsApiKey: apiKey || '',
  })

  const mapCenter = useMemo(() => {
    if (center?.lat == null || center?.lng == null) return undefined
    return { lat: Number(center.lat), lng: Number(center.lng) }
  }, [center?.lat, center?.lng])

  const handleMarkerLoad = useCallback((eventId, marker) => {
    markerRefs.current[eventId] = marker
  }, [])

  const handleMarkerUnmount = useCallback(eventId => {
    if (markerRefs.current[eventId] != null) {
      delete markerRefs.current[eventId]
    }
  }, [])

  const selectedPin = useMemo(
    () => (selectedPinEventId ? pins.find(p => p.event_id === selectedPinEventId) : null),
    [pins, selectedPinEventId],
  )

  if (!apiKey) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapNotConfigured', 'Map is not configured.')}
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapLoadError', 'Map could not be loaded.')}
        </div>
      </div>
    )
  }

  if (loading || !isLoaded) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.mapLoading', 'Loading map…')}
        </div>
      </div>
    )
  }

  if (!mapCenter) {
    return (
      <div className={className || styles.mapContainer}>
        <div className={styles.placeholder}>
          {t('profiles.communityFootprintPlaceholder', 'Your community footprint will appear here.')}
        </div>
      </div>
    )
  }

  return (
    <div className={className || styles.mapContainer}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          fullscreenControl: true,
        }}
      >
        {pins.map(pin => (
          <Marker
            key={pin.event_id}
            position={{ lat: pin.lat, lng: pin.lng }}
            title={pin.title}
            onLoad={marker => handleMarkerLoad(pin.event_id, marker)}
            onUnmount={() => handleMarkerUnmount(pin.event_id)}
            onClick={() => setSelectedPinEventId(pin.event_id)}
            icon={
              pin.thumbnail_url && typeof window !== 'undefined' && window.google?.maps?.Size
                ? {
                    url: pin.thumbnail_url,
                    scaledSize: new window.google.maps.Size(PIN_ICON_SIZE.width, PIN_ICON_SIZE.height),
                  }
                : undefined
            }
          />
        ))}
        {selectedPin && markerRefs.current[selectedPin.event_id] && (
          <InfoWindow
            anchor={markerRefs.current[selectedPin.event_id]}
            onCloseClick={() => setSelectedPinEventId(null)}
          >
            <div className={styles.infoWindow}>
              {selectedPin.thumbnail_url && (
                <img
                  src={selectedPin.thumbnail_url}
                  alt=""
                  className={styles.infoWindowThumb}
                />
              )}
              <p className={styles.infoWindowTitle}>{selectedPin.title}</p>
              <a
                href={`/events/${selectedPin.event_slug || selectedPin.event_id}`}
                className={styles.infoWindowLink}
                onClick={e => {
                  e.preventDefault()
                  navigate(`/events/${selectedPin.event_slug || selectedPin.event_id}`)
                  setSelectedPinEventId(null)
                }}
              >
                {t('profiles.mapViewEvent', 'View event')}
              </a>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  )
}

CommunityFootprintMap.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  loading: PropTypes.bool,
  profileType: PropTypes.oneOf(['user', 'business']),
  pins: PropTypes.arrayOf(
    PropTypes.shape({
      event_id: PropTypes.number.isRequired,
      event_slug: PropTypes.string,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      thumbnail_url: PropTypes.string,
      title: PropTypes.string,
      pin_type: PropTypes.string,
    })
  ),
  className: PropTypes.string,
}
